import { defineNuxtRouteMiddleware, navigateTo, useRuntimeConfig } from '#app';
import { LmsRouteNames } from '../types/types';
import { useNuxtApp } from 'nuxt/app';
import { type Auth0VueClient } from '@auth0/auth0-vue';
import useAuthStore from '~/stores/auth';
import { savePostLoginRedirect, getPostLoginRedirect, isLoggedIn, setLoggedIn, clearLoggedIn } from '~/composables/usePostLoginRedirect';

const AUTH0_POLL_INTERVAL_MS = 50;
const AUTH0_MAX_WAIT_MS = 1000;

function waitForAuth0(auth0: Auth0VueClient): Promise<void> {
   if (!auth0.isLoading.value) {
      return Promise.resolve();
   }

   return new Promise<void>(resolve => {
      let elapsed = 0;
      const interval = setInterval(() => {
         elapsed += AUTH0_POLL_INTERVAL_MS;

         if (!auth0.isLoading.value || elapsed >= AUTH0_MAX_WAIT_MS) {
            clearInterval(interval);
            resolve();
         }
      }, AUTH0_POLL_INTERVAL_MS);
   });
}

export default defineNuxtRouteMiddleware(async (to) => {
   // Cast the plugin instance to the official Auth0VueClient type
   const auth0 = useNuxtApp().$auth0 as Auth0VueClient;
   if (!auth0) {
      // Allow login routes to proceed without auth0 - they will handle initialization
      if (to.path.startsWith('/login')) {
         return;
      }
      return navigateTo('/');
   }

   const authStore = useAuthStore();
   const queryCodeAvailable = to.query.code;

   // Check if this is a Stripe payment success redirect
   if (to.query.session_id || to.query.payment_intent) {
      // Force save the Stripe success URL with query params
      savePostLoginRedirect(to.fullPath, { force: true, source: 'auth-stripe' });
   }

   // Handle direct navigation or link opened in new tab
   // Save LMS routes for post-login redirect (composable handles conflict resolution)
   if (
      to.fullPath.startsWith('/lms') &&
      !to.query.code // Exclude Auth0 callback URLs
   ) {
      // Clean URL by removing only 'portal' parameter, preserve others
      const url = new URL(to.fullPath, 'http://localhost');
      url.searchParams.delete('portal');
      const cleanUrl = url.pathname + (url.search || '');

      // Use preserveQueryParams during auth flow to avoid losing query params
      // when the URL temporarily loses them during Auth0 redirect
      savePostLoginRedirect(cleanUrl, { preserveQueryParams: true, source: 'auth-middleware' });
   }

   if (queryCodeAvailable) {
      // Auth0 callback is being processed - let the Auth0 SDK handle the redirect
      // via onRedirectCallback in the auth0 plugin. Do not navigate here to avoid
      // NAVIGATION_DUPLICATED errors from conflicting navigations.
      return;
   }

   // Instant redirect for logged-in users hitting the homepage.
   // Uses localStorage flag as a hint to avoid waiting for Auth0 to initialize.
   // If stale (session expired), user lands on /lms, Auth0 catches it, bounces to login.
   const config = useRuntimeConfig();
   const hasPortalBrand = !!(config.public.portalBrand as string);

   if (to.path === '/' && isLoggedIn()) {
      // Non-portal brands: always redirect logged-in users from / to LMS
      if (!hasPortalBrand) {
         return navigateTo(getPostLoginRedirect());
      }

      // Portal brands: only redirect during the Auth0 callback flow.
      // The auth0 plugin sets this flag before Auth0 SDK navigates to /
      // (which causes a homepage flash). Normal homepage visits don't have the flag.
      if (sessionStorage.getItem('auth0_callback_pending')) {
         sessionStorage.removeItem('auth0_callback_pending');
         const pendingRedirect = getPostLoginRedirect();
         return navigateTo(pendingRedirect);
      }
   }

   // Only wait for Auth0 on routes where auth state determines a redirect:
   // - LMS routes: unauthenticated users get redirected to login
   // - Homepage without portal brand: authenticated users get redirected to LMS
   // Login routes are excluded — they trigger loginWithRedirect regardless of auth state,
   // so waiting for Auth0 is pointless (and wastes time when silent auth fails).
   // Public pages (/blog, /about, etc.) render immediately — they handle auth reactively.
   const isLmsRoute = to.path.startsWith('/lms');
   const isLoginRoute = to.path.startsWith('/login');
   const needsAuthDecision = isLmsRoute || (to.path === '/' && !hasPortalBrand);

   if (isLoggedIn() && needsAuthDecision) {
      await waitForAuth0(auth0);

      // If Auth0 timed out and user is not authenticated, the isLoggedIn hint is stale
      // (session expired). Clear it so subsequent navigations don't wait again.
      if (!auth0.isAuthenticated.value) {
         clearLoggedIn();
      }
   }

   if (auth0.error.value) {
      console.error('[auth-mw] Auth0 SDK error:', auth0.error.value);
   }

   const isAuthenticated = auth0.isAuthenticated.value;

   // If authenticated and we need to fetch the token, do it now
   if (isAuthenticated) {
      setLoggedIn();

      // Handle token fetching - this one we DO need to wait for in most cases
      if (!authStore.accessToken) {
         try {
            const token = await auth0.getAccessTokenSilently();
            authStore.accessToken = token;
         } catch (e) {
            console.error('[auth-mw] failed to get access token:', e);
         }
      }

      // Start profile fetch but don't block navigation
      if (authStore.accessToken && !authStore.isFetchingUserProfile) {
         authStore.isFetchingUserProfile = true;

         // Don't await, just fire and continue
         authStore.setCurrentUserProfile();

         // Persist portal selection to server after auth (covers deep links, login page)
         const selectedPortal = localStorage.getItem('selectedPortal');
         if (selectedPortal) {
            authStore.persistPortalSelection(selectedPortal);
         }
      }

      // Start subscription fetch but don't block navigation
      if (authStore.accessToken && !authStore.isFetchingUserSubscriptions) {
         authStore.setCurrentUserSubscriptions();
      }
   }

   const shouldRedirectToBrandLogin = !isAuthenticated && isLmsRoute;

   if (shouldRedirectToBrandLogin) {
      const portalBrand = config.public.portalBrand as string;
      if (hasPortalBrand) {
         return navigateTo(`/login/${portalBrand}`);
      }

      return navigateTo({
         name: LmsRouteNames.brandLogin,
         params: {
            portal: localStorage.getItem('portal') || 'ipapolo',
         },
      });
   }

   // Redirect authenticated users away from non-LMS routes back to their LMS destination.
   // Covers homepage (/) and login pages (/login/*) that authenticated users shouldn't stay on.
   // Portal brands skip the homepage redirect — authenticated users stay on the public homepage.
   if (isAuthenticated && !isLmsRoute) {
      if (isLoginRoute || (to.path === '/' && !hasPortalBrand)) {
         return navigateTo(getPostLoginRedirect());
      }
   }

   // After logout, Auth0 redirects all non-USPA users to origin.
   // IPA portals (ipa, ipapolo, default) stay on the public homepage.
   // Other portals (swiss, nz, etc.) get redirected to their login page via localStorage.
   // Branded builds (PI, All Ireland, etc.) are skipped — they have their own homepage.
   if (!isAuthenticated && to.path === '/' && !hasPortalBrand) {
      const portal = localStorage.getItem('portal') || 'default';
      const ipaPortals = ['ipa', 'ipapolo', 'default'];
      if (!ipaPortals.includes(portal)) {
         return navigateTo(`/login/${portal}`);
      }
   }
});
